import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import FormContent from './WebformContent';
import { gql } from '@generated/gql';
import {
  WebformQueryDocument,
  WebformSubmissionMutationDocument,
} from '@generated/graphql';
import { Spinner } from '@components/Spinner';
import { IProps } from '@components/Webform/Webform';

const FR_MESSGAGE = 'Merci beaucoup. Vos renseignements ont été transmis.';
const DE_MESSGAGE = 'Vielen Dank. Ihre Angaben wurden übermittelt.';
const DE_ERROR_MSG = 'Das Formular konnte nicht geladen werden.';
const FR_ERROR_MSG = "Le formulaire n'a pas pu être chargé.";

const JSON_URL = 'submit?_format=json';

const Webform: React.FC<IProps.Webform> = ({ webform, lang }) => {
  const { data, loading } = useQuery(WebformQueryDocument, {
    variables: { target: webform?.targetId },
  });

  const [state, setState] = React.useState({
    isSubmitted: false,
    loading: false,
    message: lang === 'DE' ? DE_MESSGAGE : FR_MESSGAGE,
    updatedInputs: [],
    urlParts: {
      jsonUrl: JSON_URL,
    },
    values: {
      webform_id: webform.targetId,
    },
  });

  const [submitWebform, { loading: mutationLoading, error: mutationError }] =
    useMutation(WebformSubmissionMutationDocument);

  const handleChange = (event: any): void => {
    const stateValues: typeof state.values & Record<string, any> = state.values;
    const target = event.target;
    const value = target.value;
    let name;
    target.type === 'checkbox'
      ? (name = target.name.split('[')[0])
      : (name = target.name);
    target.type === 'checkbox'
      ? (stateValues[name] = checkValue(stateValues[name], value))
      : (stateValues[name] = value);
    setState({
      ...state,
      values: stateValues,
    });
  };

  const checkValue = (arr: any, value: string) => {
    if (!arr) {
      arr = [];
      arr.push(value);
      return arr;
    }
    arr.indexOf(value) > -1
      ? arr.splice(arr.indexOf(value), 1)
      : arr.push(value);
    return arr;
  };

  const inputs = data?.webformById?.elements;

  if (!inputs) {
    return <div>{(lang === 'DE' && DE_ERROR_MSG) || FR_ERROR_MSG}</div>;
  }
  return (
    <form
      onSubmit={(e) => {
        setState({
          ...state,
          loading: true,
          isSubmitted: true,
        });

        e.preventDefault();
        submitWebform({
          variables: {
            values: JSON.stringify(state.values),
          },
        }).then(() => {
          setState({
            ...state,
            loading: false,
            isSubmitted: true,
          });
        });
      }}
    >
      {!state.isSubmitted &&
        inputs.map((item: any, index: number) => {
          if (!item.title && !item.markup) {
            return false;
          }
          return (
            <FormContent
              handleChange={(e) => handleChange(e)}
              item={item}
              key={`formContent-${index}`}
              lang={lang}
            />
          );
        })}
      {mutationLoading && <Spinner />}
      {mutationError != null && <p>Error – Please try again</p>}
      {state.isSubmitted ? (
        <p>
          <strong>{state.message}</strong>
        </p>
      ) : null}
    </form>
  );
};

export const WebformQuery = gql(/* GraphQL */ `
  query WebformQuery($target: String!) {
    webformById(webform_id: $target) {
      title
      description
      elements {
        ... on WebformElement {
          id
          type
        }
        ... on WebformElementActions {
          submitLabel
          title
        }
        ... on WebformElementTextBase {
          title
          defaultValue
          required {
            message
          }
          size
          minLength
          maxLength
          pattern {
            message
            rule
          }
          placeholder
          multiple {
            limit # 0 means Unlimited config.
            message
          }
        }
        ... on WebformElementMarkup {
          markup
        }
        ... on WebformElementTextarea {
          rows
        }
        ... on WebformElementHidden {
          defaultValue
        }
        ... on WebformElementDate {
          dateMin
          dateMax
          step
          defaultValue
          title
          multiple {
            limit # 0 means Unlimited config.
            message
          }
        }
        ... on WebformElementOptionsBase {
          title
          defaultValue
          options {
            title
            value
          }
          required {
            message
          }
          multiple {
            limit # 0 means Unlimited config.
            message
          }
        }
        ... on WebformElementSelect {
          emptyOption {
            title
            value
          }
        }
        ... on WebformElementManagedFile {
          title
          fileExtensions
          required {
            message
          }
          multiple {
            limit # 0 means Unlimited config.
            message
          }
        }
        ... on WebformElementTermSelect {
          title
          termOptions(depth: 1) {
            entityId
            entityLabel
          }
        }
        ... on WebformElementComposite {
          elements {
            id
            type
          }
        }
        ... on WebformElementNumber {
          required {
            message
          }
          min
          max
          size
          step
        }
      }
    }
  }
`);

export const WebformSubmissionMutation = gql(/* GraphQL */ `
  mutation WebformSubmissionMutation($values: String!) {
    submitForm(values: $values) {
      errors
      submission {
        id
      }
    }
  }
`);

export default Webform;
